import { CreditCard, FileCheck, FlaskConical, Globe, GraduationCap, Mail, ShieldAlert, Users } from 'lucide-react';

/*
 * Content for the Education scenario walkthrough — Alderwick State University.
 *
 * PROVENANCE, and it differs from the other packs: there is no `education` demo
 * pack yet. Halcyon, Kestrel, Nordvik and Godwit take their artifact strings
 * verbatim from authored pack YAML. These are INVENTED, designed against the
 * seed specification at docs/industry-packs/education-ORG-seed.md so that the
 * pack, once authored, matches this page rather than the other way round.
 *
 * When the pack lands, re-derive this file from the YAML and delete this note.
 */

export const alderwickScenario = {
  org: {
    name: 'Alderwick State University',
    subtitle: 'Five situations a university actually faces, from the registrar to the research enclave.',
    framingNote: 'a composite organization in the BOSSS demo environment',
    profile: [
      { icon: GraduationCap, text: '~18,000 students' },
      { icon: Users, text: '~2,400 faculty and staff' },
      { icon: FlaskConical, text: 'Federally funded research institute' },
      { icon: Globe, text: '~4,200 fully online students' },
    ],
  },
  scenarios: [
    {
      id: 'finals-week-ransomware',
      number: 1,
      title: 'Ransomware During Finals Week',
      tagline: 'The student information system encrypts, and grades cannot be submitted',
      icon: ShieldAlert,
      frameworks: ['FERPA', 'NIST CSF 2.0'],
      situation: {
        narrative: 'A phishing email compromises a registrar workstation. Three days later the attacker moves '
          + 'laterally and encrypts the student information system and the shared academic file store — during '
          + 'finals week. Grade submission stops, registration for the next term stops, and an end-of-life '
          + 'integration layer turns out to be the reason the blast radius reached as far as it did.',
        cast: [
          { name: 'Theodore Nkemelu', role: 'Chief Information Security Officer' },
          { name: 'Deborah Cranfield', role: 'University Registrar' },
          { name: 'Raymond Dunleavy', role: 'IT Infrastructure Director' },
          { name: 'Kenji Nakashima', role: 'SOC Manager' },
        ],
      },
      timing: [
        { at: '−21 days', label: 'Phishing email to registrar staff' },
        { at: '−19 days', label: 'Lateral movement' },
        { at: '−18 days', label: 'SIS encrypted — finals week' },
        { at: '−3 days', label: 'Restore complete, trustees reported' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Mass encryption on the student information system is detected',
          artifact: 'Ransomware encryption of the SIS production cluster',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Escalated to a full investigation case',
          artifact: 'Ransomware Investigation — Finals Week SIS Outage',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Academic outage ticket opens and downtime procedures are invoked',
          artifact: 'SIS outage — grade submission and registration halted',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record covering the academic systems affected',
          artifact: 'Ransomware Encryption of the Student Information System',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The legacy integration layer that widened the blast radius becomes its own finding',
          artifact: 'End-of-Life SIS Integration Layer Widened Ransomware Exposure',
        },
        {
          module: 'BCRP',
          href: '/platform/resilience',
          action: 'Continuity and recovery plans drive the restore from the DR site',
          artifact: 'Academic Continuity Plan',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Board of trustees reporting closes the loop',
          artifact: 'Board of Trustees Security Report — Ransomware Incident',
        },
      ],
      enables: [
        'The registrar, the CISO and infrastructure work one incident, so academic impact and security response are the same timeline rather than two.',
        'The legacy system that widened the damage is recorded as a finding with an owner, which is how a budget-constrained upgrade finally gets funded.',
      ],
      impact: {
        entities: [
          { name: 'Alderwick State University', detail: 'Registrar, Academic Affairs', location: 'Ellerton, OR' },
        ],
        locations: [
          { name: 'Alderwick Main Campus', detail: 'Headquarters · registrar, academic affairs', location: 'Ellerton, OR' },
          { name: 'Campus Data Center', detail: 'Data center · SIS hosting', location: 'Ellerton, OR' },
          { name: 'Alderwick DR / Backup Site', detail: 'Disaster Recovery Site · restore source', location: 'Fairmont, OR' },
        ],
        applications: [
          { name: 'ScholarisSIS Production Cluster', detail: 'Encrypted', location: 'Ellerton, OR' },
          { name: 'Academic shared file store', detail: 'Encrypted', location: 'Ellerton, OR' },
          { name: 'Quillmark Learning Management System', detail: 'Grade passback halted', location: 'Ellerton, OR' },
          { name: 'Registrar workstation', detail: 'Initial compromise', location: 'Ellerton, OR' },
        ],
        vendors: [
          { name: 'Scholaris Student Systems', detail: 'Student information system', location: 'Madison, WI' },
          { name: 'Quillmark Learning', detail: 'Learning management', location: 'Portland, OR' },
          { name: 'Fernwood Endpoint Defense', detail: 'Endpoint detection', location: 'Seattle, WA' },
          { name: 'Driftwood Security Analytics', detail: 'SIEM', location: 'Boulder, CO' },
        ],
        data: [
          { name: 'Student Education Record', detail: '~158,000 current and alumni records', location: 'Ellerton, OR' },
          { name: 'Grade & Transcript Record', detail: '~1.2M course-grade records', location: 'Ellerton, OR' },
          { name: 'Housing Assignment', detail: '~4,100 residents', location: 'Ellerton, OR' },
        ],
      },
    },
    {
      id: 'ferpa-breach',
      number: 2,
      title: 'FERPA Breach',
      tagline: 'A compromised mailbox and a bulk export of education records',
      icon: Mail,
      frameworks: ['FERPA', 'Oregon Consumer Privacy Act', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Phishing compromises a student-records specialist\'s mailbox. A bulk export of education '
          + 'records follows — names, student identifiers, and academic history. FERPA governs the disclosure, '
          + 'state law governs the notification, and the enabling gap is multi-factor authentication that was '
          + 'never fully rolled out to staff email.',
        cast: [
          { name: 'Alan Mbeki', role: 'Associate Registrar, Records' },
          { name: 'Josephine Tran', role: 'Student Records Specialist' },
          { name: 'Sandra Bellweather', role: 'General Counsel' },
          { name: 'Marcus Oyelowo', role: 'VP Student Affairs' },
        ],
      },
      timing: [
        { at: '−45 days', label: 'Mailbox compromise' },
        { at: '−38 days', label: 'Bulk export discovered' },
        { at: '−25 days', label: 'Student notification prepared' },
        { at: '−10 days', label: 'Notification issued' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'The bulk export of student records is flagged as exfiltration',
          artifact: 'Bulk student education record export from compromised mailbox',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Worked as a records-disclosure investigation',
          artifact: 'FERPA Breach Investigation — Registrar Mailbox Compromise',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record establishes what was disclosed and to whom',
          artifact: 'Education Record Exfiltration via Compromised Registrar Mailbox',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The enabling control gap is tracked separately from the breach',
          artifact: 'Incomplete MFA Coverage on Staff Email Accounts',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Notification is produced against the education-records policy',
          artifact: 'FERPA Breach Notification to Affected Students',
        },
      ],
      enables: [
        'The disclosure and the authentication gap that allowed it are separate records with separate owners, so closing one does not silently close the other.',
        'Directory-information opt-out state lives on the student record, which is what makes a disclosure assessment possible rather than approximate.',
      ],
      impact: {
        entities: [
          { name: 'Alderwick State University', detail: 'Registrar\'s Office', location: 'Ellerton, OR' },
        ],
        locations: [
          { name: 'Alderwick Main Campus', detail: 'Headquarters · registrar', location: 'Ellerton, OR' },
        ],
        applications: [
          { name: 'Staff email mailbox', detail: 'Compromised', location: 'Ellerton, OR' },
          { name: 'ScholarisSIS Production Cluster', detail: 'Export source', location: 'Ellerton, OR' },
          { name: 'Northgate Identity SSO', detail: 'MFA coverage gap', location: 'Ellerton, OR' },
        ],
        vendors: [
          { name: 'Northgate Identity', detail: 'SSO / IdP and InCommon federation', location: 'Denver, CO' },
          { name: 'Scholaris Student Systems', detail: 'Student information system', location: 'Madison, WI' },
        ],
        data: [
          { name: 'Student Education Record', detail: '~158,000 records', location: 'Ellerton, OR' },
          { name: 'Student Social Security Number', detail: '~158,000 records', location: 'Ellerton, OR' },
          { name: 'Grade & Transcript Record', detail: '~1.2M course-grade records', location: 'Ellerton, OR' },
        ],
      },
    },
    {
      id: 'controlled-research',
      number: 3,
      title: 'Controlled Research Data',
      tagline: 'Anomalous access to a federally funded dataset under NIST 800-171',
      icon: FlaskConical,
      frameworks: ['NIST 800-171', 'Export Control', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Anomalous access to a controlled research dataset on the high-performance computing enclave '
          + 'triggers a review. The work is federally funded and carries controlled unclassified information '
          + 'obligations, so the question is not only whether data moved but whether an export-control '
          + 'determination was ever made for the collaborator involved.',
        cast: [
          { name: 'Dr. Nathaniel Ferro', role: 'Director, Research Computing' },
          { name: 'Lucia Marchetti', role: 'Research Security & Export Control Officer' },
          { name: 'Dr. Karen Whitlock', role: 'VP Research' },
          { name: 'Owen Bramhall', role: 'HPC Systems Administrator' },
        ],
      },
      timing: [
        { at: '−60 days', label: 'Anomalous enclave access detected' },
        { at: '−56 days', label: 'Export-control review opened' },
        { at: '+45 days', label: 'Access-control remediation targeted' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Unusual access to the controlled enclave is detected',
          artifact: 'Anomalous access to controlled research dataset',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Worked as a research data-handling investigation',
          artifact: 'Controlled Research Data Access Review',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The access-control weakness is recorded against the federal requirement',
          artifact: 'NIST 800-171 3.1.1 — Access Control Gap on Research Enclave',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A missing export-control determination is a separate finding with a separate owner',
          artifact: 'Export-Control Determination Not Recorded for Collaborator Access',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'The system security plan governs how controlled research is handled',
          artifact: 'Research Enclave System Security Plan',
        },
      ],
      enables: [
        'The research institute is its own entity, so the 800-171 programme scopes to the enclave instead of dragging the whole university into federal control requirements.',
        'A security detection and an export-control obligation reference the same access event, which is what lets research security and information security work one review.',
      ],
      impact: {
        entities: [
          { name: 'Alderwick Research Institute', detail: 'CUI enclave — 800-171 scope', location: 'Ellerton, OR' },
        ],
        locations: [
          { name: 'Alderwick Research Institute', detail: 'Office · controlled research', location: 'Ellerton, OR' },
          { name: 'Campus Data Center', detail: 'Data center · HPC hosting', location: 'Ellerton, OR' },
        ],
        applications: [
          { name: 'HPC research enclave', detail: 'Controlled access', location: 'Ellerton, OR' },
          { name: 'Research data store', detail: 'Controlled datasets', location: 'Ellerton, OR' },
          { name: 'Northgate Identity SSO', detail: 'InCommon federated access', location: 'Ellerton, OR' },
        ],
        vendors: [
          { name: 'SummitCompute Research Cloud', detail: 'HPC and research storage', location: 'Austin, TX' },
          { name: 'Lanternhouse Assurance Group', detail: 'Independent assessor', location: 'Chicago, IL' },
        ],
        data: [
          { name: 'Controlled Research Dataset', detail: '~340 controlled datasets', location: 'Ellerton, OR' },
          { name: 'Grant Award Record', detail: '~420 active awards', location: 'Ellerton, OR' },
        ],
      },
    },
    {
      id: 'campus-payments',
      number: 4,
      title: 'Campus Payment Card Compromise',
      tagline: 'Skimming on campus-card terminals, across dining and athletics',
      icon: CreditCard,
      frameworks: ['PCI DSS v4.0', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Card skimming is discovered on campus payment terminals across dining, athletics and the '
          + 'bookstore. The scope review that follows finds two PCI DSS v4.0 gaps: cardholder data at rest is '
          + 'not fully rendered unreadable, and terminal inspection has not been performed on the schedule the '
          + 'standard requires.',
        cast: [
          { name: 'Gregory Painter', role: 'Bursar' },
          { name: 'Amara Osei', role: 'Student Accounts Analyst' },
          { name: 'Theodore Nkemelu', role: 'Chief Information Security Officer' },
        ],
      },
      timing: [
        { at: '−30 days', label: 'Skimming discovered on terminals' },
        { at: '−26 days', label: 'PCI scope review opened' },
        { at: '+60 days', label: 'QSA assessment window' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Terminal tampering indicators are detected across sites',
          artifact: 'Card skimming indicators on campus payment terminals',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Cardholder data at rest is recorded as an open requirement gap',
          artifact: 'PCI DSS Req 3.5.1 — Cardholder Data at Rest Not Rendered Unreadable',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Missing terminal inspection is a distinct finding with its own owner',
          artifact: 'PCI DSS Req 9.5.1 — Payment Terminal Inspection Cadence Not Met',
        },
        {
          module: 'CMDB',
          href: '/platform/cmdb',
          action: 'Payment terminals are inventory with locations, which is what makes inspection auditable',
          artifact: 'Campus payment terminal fleet',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A certification project carries the programme to the QSA assessment',
          artifact: 'PCI DSS v4.0 QSA Certification — Alderwick State University',
        },
      ],
      enables: [
        'Terminal inspection becomes checkable because every terminal is an inventory record with a location and an owner, rather than a device someone remembers installing.',
        'Payment scope spans dining, athletics and the bookstore under one programme instead of three departments each assuming another handled it.',
      ],
      impact: {
        entities: [
          { name: 'Alderwick State University', detail: 'Bursar, Auxiliary Services', location: 'Ellerton, OR' },
        ],
        locations: [
          { name: 'Alderwick Main Campus', detail: 'Headquarters · bursar, dining, bookstore', location: 'Ellerton, OR' },
          { name: 'Alderwick Athletics Complex', detail: 'Office · event and concession payments', location: 'Ellerton, OR' },
          { name: 'Brookhaven Regional Center', detail: 'Branch Office · regional payments', location: 'Brookhaven, OR' },
        ],
        applications: [
          { name: 'Campus payment terminal fleet', detail: 'Skimming discovered', location: 'Ellerton, OR' },
          { name: 'TuitionBridge payment gateway', location: 'Ellerton, OR' },
          { name: 'Kelvary campus card platform', location: 'Ellerton, OR' },
        ],
        vendors: [
          { name: 'TuitionBridge Payments', detail: 'Payment gateway', location: 'Atlanta, GA' },
          { name: 'Kelvary Campus Card', detail: 'Campus card and access control', location: 'Columbus, OH' },
          { name: 'Lanternhouse Assurance Group', detail: 'PCI QSA', location: 'Chicago, IL' },
        ],
        data: [
          { name: 'Tuition Payment Card PAN', detail: '~24,000 card payments/year', location: 'Ellerton, OR' },
          { name: 'Campus Card Transaction', detail: '~2.8M transactions/year', location: 'Ellerton, OR' },
        ],
      },
    },
    {
      id: 'glba-safeguards',
      number: 5,
      title: 'GLBA Safeguards Audit',
      tagline: 'The financial-aid data programme every Title IV institution owes',
      icon: FileCheck,
      frameworks: ['GLBA Safeguards Rule', 'FERPA', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Participation in federal financial aid brings the GLBA Safeguards Rule with it: a written '
          + 'information security programme covering student financial data, assessed annually. The cycle runs '
          + 'across policies, procedures and access reviews, with the annual SIS access-review campaign standing '
          + 'as the evidence the assessment cites.',
        cast: [
          { name: 'Renata Salcedo', role: 'Director, Financial Aid' },
          { name: 'Desmond Achara', role: 'GRC Analyst' },
          { name: 'Sandra Bellweather', role: 'General Counsel' },
        ],
      },
      timing: [
        { at: '−90 days', label: 'Assessment kickoff' },
        { at: '−14 days', label: 'Report finalized' },
        { at: 'Open now', label: 'SIS access-review campaign' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The programme policy sits under governance with attestation history',
          artifact: 'GLBA Safeguards Information Security Program',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A formal assessment runs against the authority itself',
          artifact: 'GLBA Safeguards Rule — Annual Program Assessment (2026)',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Identified gaps become a plan of action with owners and dates',
          artifact: 'POA&M — GLBA Safeguards Risk Assessment Gaps',
        },
        {
          module: 'IAM',
          href: '/platform/iam',
          action: 'Annual recertification of access to student financial data is the cited evidence',
          artifact: 'Annual SIS Access Review — Financial Aid and Student Records',
        },
      ],
      enables: [
        'The access review the assessment cites is a live campaign record rather than a screenshot pasted into an appendix.',
        'FERPA and the Safeguards Rule map into one control taxonomy, so a single access control satisfies both instead of being evidenced twice.',
      ],
      impact: {
        entities: [
          { name: 'Alderwick State University', detail: 'Whole-institution scope', location: 'Ellerton, OR' },
          { name: 'Alderwick Online', detail: 'Distance-learning division' },
        ],
        locations: [
          { name: 'Alderwick Main Campus', detail: 'Headquarters · financial aid', location: 'Ellerton, OR' },
          { name: 'Campus Data Center', detail: 'Data center', location: 'Ellerton, OR' },
        ],
        applications: [
          { name: 'ScholarisSIS Production Cluster', detail: 'Financial aid records', location: 'Ellerton, OR' },
          { name: 'Thornmere Public ERP', detail: 'Finance and payroll', location: 'Ellerton, OR' },
          { name: 'Northgate Identity SSO', detail: 'Access recertification', location: 'Ellerton, OR' },
        ],
        vendors: [
          { name: 'Lanternhouse Assurance Group', detail: 'Independent assessor', location: 'Chicago, IL' },
          { name: 'Thornmere Public ERP', detail: 'Finance, HR, payroll', location: 'Kansas City, MO' },
        ],
        data: [
          { name: 'Financial Aid Application (FAFSA)', detail: '~11,200 applications/year', location: 'Ellerton, OR' },
          { name: 'Student Social Security Number', detail: '~158,000 records', location: 'Ellerton, OR' },
          { name: 'Student Education Record', detail: '~158,000 records', location: 'Ellerton, OR' },
        ],
      },
    },
  ],
};
